import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Grid } from '@mui/material';

import {
    useChangeable,
    FullForm,
    TextField,
    EmailField,
    PhoneField,
    AutocompleteField,
    BoolField
} from '../CommonComponents/Form';
import { Popup, SectionDivider } from '../CommonComponents';

import {
    createUser,
    deleteUser,
    getUser,
    updateUser
} from '../../api-admin/users';

import toast from '../../utils/toast';
import { UserType } from '../../types';
import { getTenant, searchTenants } from '../../api-admin/tenants';

const defaultUser = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    website: '',
    billingEmail: '',
    billingMark: '',
    billingStreet: '',
    billingZip: '',
    billingCity: ''
};

const UserForm: React.FC = () => {
    const { id, tenantId } = useParams();
    const navigate = useNavigate();

    const [entity, onChange] = useChangeable(defaultUser);
    const [user, setUser] = React.useState<UserType | null>(null);
    const [createdUserId, setCreatedUserId] = React.useState<number | null>(null);
    const [showWatchlistCreationInfo, setShowWatchlistCreationInfo] = React.useState(false);
    const [showWatchlistDeletionInfo, setShowWatchlistDeletionInfo] = React.useState(false);

    React.useEffect(() => {
        (async () => {
            if (id) {
                const u = await getUser(id);
                onChange(u);
                setUser(u);
            }
        })();
    }, [id, onChange]);

    React.useEffect(() => {
        (async () => {
            if (tenantId) {
                onChange({ tenantId });
            }
        })();
    }, [onChange, tenantId]);

    React.useEffect(() => {
        if (!tenantId) {
            return undefined;
        }
        let active = true;
        (async () => {
            const tenant = await getTenant(tenantId);
            if (active) {
                onChange({
                    tenantId: tenant.id,
                    tenantName: tenant.name,
                    tenantOrgNumber: tenant.orgNumber,
                    tenantIsAccepted: tenant.isAccepted
                });
            }
        })();
        return () => {
            active = false;
        };
    }, [onChange, tenantId]);

    const onSubmit = React.useCallback(async () => {
        try {
            if (id) {
                await updateUser(id, entity);
                toast('Användaren uppdaterad');
                navigate(-1);
            } else {
                const createdUser = await createUser(entity);
                toast('Användaren skapad');
                setCreatedUserId(createdUser.id);
                console.log({ entity });
                if (entity.tenantIsAccepted) {
                    setShowWatchlistCreationInfo(true);
                } else {
                    navigate(`/users/${createdUser.id}`, { replace: true });
                }
            }
        } catch (e: any) {
            toast(e, `Kunde inte ${id ? 'uppdatera' : 'skapa'} användaren, försök igen`);
        }
    }, [id, entity, navigate]);

    const onDelete = React.useCallback(async () => {
        try {
            if (id) {
                await deleteUser(id);
                toast('Användaren borttagen');
                setShowWatchlistDeletionInfo(true);
            }
        } catch (e: any) {
            toast(e, 'Kunde inte ta bort användaren, försök igen');
        }
    }, [id, navigate]);

    return (
        <FullForm
            title={`${id ? 'Redigera' : 'Skapa'} användare`}
            entity={entity}
            onSubmit={onSubmit}
            onDelete={id ? onDelete : undefined}
        >
            <Popup
                open={showWatchlistCreationInfo}
                title="Skapade bevakningar"
                body={
                    'Bevakningar har skapats åt den nya användaren för alla '
                    + 'uppdrag och kapaciteter som publiceras i företagets grupper.'
                }
                okLabel="Ok"
                handleOk={() => navigate(`/users/${createdUserId}`, { replace: true })}
            />
            <Popup
                open={showWatchlistDeletionInfo}
                title="Borttagna bevakningar"
                body="Samtliga bevakningar knutna till användaren har tagit bort."
                okLabel="Ok"
                handleOk={() => navigate(-2)}
            />
            <Grid container spacing={2}>
                <Grid item md={12} lg={6}>
                    <SectionDivider label="Kontaktuppgifter" marginTop={1} />
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <AutocompleteField<{
                                id: number | string,
                                name: string,
                                orgNumber?: string,
                                isAccepted?: boolean
                            }>
                                label="Företag"
                                optionIdKey="id"
                                defaultOption={!entity.tenantId ? null : {
                                    id: entity.tenantId,
                                    name: entity.tenantName || ''
                                }}
                                fetchOptions={(query) => searchTenants(query || '')}
                                minQueryLength={3}
                                getOptionName={(o) => o.name}
                                getOptionDescription={(o) => o.orgNumber || ''}
                                onChange={(tenant) => onChange({
                                    tenantId: tenant === null ? null : tenant.id,
                                    tenantName: tenant === null ? null : tenant.name,
                                    tenantOrgNumber: tenant === null ? null : tenant.orgNumber,
                                    tenantIsAccepted: tenant === null ? false : !!tenant.isAccepted
                                })}
                                disabled={!!id || !!tenantId}
                                required
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                name="firstName"
                                label="Förnamn"
                                value={entity.firstName}
                                onChange={onChange}
                                required
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                name="lastName"
                                label="Efternamn"
                                value={entity.lastName}
                                onChange={onChange}
                                required
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                name="title"
                                label="Titel"
                                value={entity.title}
                                onChange={onChange}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <EmailField
                                name="email"
                                label="E-post"
                                value={entity.email}
                                onChange={onChange}
                                required
                                helperText={
                                    user?.emailVerified && entity.email !== user?.email
                                        ? 'Användaren måste verifiera sin e-post igen om du ändrar den.'
                                        : ''
                                }
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <PhoneField
                                name="phone"
                                label="Mobil"
                                value={entity.phone}
                                onChange={onChange}
                                helperText={
                                    user?.phoneVerified && entity.phone && entity.phone !== user?.phone
                                        ? 'Användaren måste verifiera sitt mobilnummer igen om du ändrar det.'
                                        : ''
                                }
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                name="phoneAlt"
                                label="Telefon"
                                maxLength={20}
                                value={entity.phoneAlt}
                                onChange={onChange}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item md={12} lg={6}>
                    <Grid container spacing={2}>
                        {!id && (
                            <Grid item xs={12}>
                                <SectionDivider label="Övrigt" marginTop={1} />
                                <BoolField
                                    name="sendInvitation"
                                    label="Skicka inbjudan"
                                    value={entity.sendInvitation}
                                    onChange={onChange}
                                    helperText="Användaruppgifter skickas till e-postadressen när användaren skapats."
                                />
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </Grid>
        </FullForm>
    );
};

export default UserForm;
